import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export const PrivacyDataCard = () => {
  const { user, fetchMe, logout } = useAuth();
  const navigate = useNavigate();
  const [savingConsent, setSavingConsent] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isClient = user?.role === "client";
  const marketingOn = user?.marketing_accepted === true;

  const toggleMarketing = async (checked) => {
    setSavingConsent(true);
    try {
      await api.patch("/auth/me/consents", { marketing_accepted: checked });
      await fetchMe();
      toast.success(checked ? "Consenso marketing attivato" : "Consenso marketing revocato");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingConsent(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/auth/me/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marketing-parrucchieri-dati-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Esportazione completata");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me", { data: { password, confirm } });
      toast.success("Account eliminato. Ci dispiace vederti andare.");
      setOpen(false);
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="rounded-3xl border border-[var(--mp-border)] bg-white p-6 space-y-5"
      data-testid="privacy-data-card"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[var(--mp-muted)]" />
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          Privacy e i tuoi dati
        </div>
      </div>

      {isClient && (
        <div className="flex items-start justify-between gap-4 rounded-2xl bg-[var(--mp-dash-bg)] p-4">
          <div className="flex items-start gap-3 min-w-0">
            <Mail className="h-4 w-4 mt-0.5 text-[var(--mp-muted)] shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">Comunicazioni marketing</div>
              <div className="text-xs text-[var(--mp-muted)] leading-relaxed">
                Novità, consigli e offerte via email. Puoi cambiare idea quando vuoi: le email di
                servizio (verifica, reset password, avvisi sull'account) arrivano comunque.
                {user?.marketing_accepted === null && (
                  <span className="block mt-1 italic">
                    Non hai ancora espresso una scelta.
                  </span>
                )}
              </div>
            </div>
          </div>
          <Switch
            checked={marketingOn}
            disabled={savingConsent}
            onCheckedChange={toggleMarketing}
            data-testid="marketing-consent-switch"
            aria-label="Consenso marketing"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={exportData}
          disabled={exporting}
          data-testid="export-data-btn"
        >
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Scarica i miei dati
        </Button>
        {user?.role !== "admin" && (
          <Button
            variant="outline"
            className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
            onClick={() => setOpen(true)}
            data-testid="delete-account-open-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Elimina il mio account
          </Button>
        )}
      </div>
      <p className="text-xs text-[var(--mp-muted)] leading-relaxed">
        L'esportazione contiene il tuo profilo, i messaggi, le idee, i post programmati e il log
        degli accessi (formato JSON). L'eliminazione è definitiva e rimuove account, messaggi,
        media e contatto email dai nostri sistemi.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Eliminare definitivamente l'account?</DialogTitle>
            <DialogDescription>
              Perderai l'accesso al portale, la cronologia dei messaggi, i contenuti programmati
              e il collegamento Telegram. Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="password"
              placeholder="La tua password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="delete-account-password"
            />
            <Input
              placeholder='Scrivi ELIMINA per confermare'
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="delete-account-confirm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-account-cancel">
              Annulla
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting || !password || confirm.trim().toUpperCase() !== "ELIMINA"}
              onClick={deleteAccount}
              data-testid="delete-account-submit"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
